import { FiPlus } from '@react-icons/all-files/fi/FiPlus'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { deleteRunner } from '~/services/runnerService'
import { Modal, ModalBody } from '../modals/CurieModal'
import { Button } from '../common/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../common/dropdowns/DropdownMenu'
import { List, ListColumn } from '../common/List'
import { Tag } from '../common/Tag'
import Typography from '../common/Typography'

const AlertIcon = () => (
  <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M12 1.5C6.20156 1.5 1.5 6.20156 1.5 12C1.5 17.7984 6.20156 22.5 12 22.5C17.7984 22.5 22.5 17.7984 22.5 12C22.5 6.20156 17.7984 1.5 12 1.5ZM12.75 17.0625C12.75 17.1656 12.6656 17.25 12.5625 17.25H11.4375C11.3344 17.25 11.25 17.1656 11.25 17.0625V10.6875C11.25 10.5844 11.3344 10.5 11.4375 10.5H12.5625C12.6656 10.5 12.75 10.5844 12.75 10.6875V17.0625ZM12 9C11.7056 8.99399 11.4253 8.87282 11.2192 8.6625C11.0132 8.45218 10.8977 8.16945 10.8977 7.875C10.8977 7.58055 11.0132 7.29782 11.2192 7.0875C11.4253 6.87718 11.7056 6.75601 12 6.75C12.2944 6.75601 12.5747 6.87718 12.7808 7.0875C12.9868 7.29782 13.1023 7.58055 13.1023 7.875C13.1023 8.16945 12.9868 8.45218 12.7808 8.6625C12.5747 8.87282 12.2944 8.99399 12 9Z'
      fill='#D94B36'
    />
  </svg>
)

export interface Runner {
  id: number
  name: string
  uuid: string
  org_id: number
  active: boolean
  os: string
  arch: string
}

export interface RunnersListProps {
  runners: Runner[]
  onAddRunner: () => void
  onEditRunner: (runner: Runner) => void
  onConfigureRunner: (runner: Runner) => void
}

export const RunnersList: React.FC<RunnersListProps> = ({
  runners,
  onAddRunner,
  onEditRunner,
  onConfigureRunner
}) => {
  const { currentWorkspace } = useWorkspaceContext()
  const queryClient = useQueryClient()
  const [isOpen, setIsOpen] = useState(false)
  const [runnerToDelete, setRunnerToDelete] = useState<Runner | null>(null)

  const deleteRunnerMutation = useMutation({
    mutationFn: (runnerId: number) => {
      if (!currentWorkspace?.uuid) {
        throw new Error('Workspace not found')
      }
      return deleteRunner(currentWorkspace.uuid, runnerId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['runners'] })
      toast.success('Runner deleted successfully')
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete runner: ${error.message}`)
    }
  })

  const handleEditRunner = (runner: Runner) => {
    onEditRunner(runner)
  }

  const handleConfigure = (runner: Runner) => {
    onConfigureRunner(runner)
  }

  const handleDeleteRunner = (runner: Runner) => {
    setRunnerToDelete(runner)
    setIsOpen(true)
  }

  const handleConfirmDelete = () => {
    if (runnerToDelete) {
      deleteRunnerMutation.mutate(runnerToDelete.id)
      setRunnerToDelete(null)
      setIsOpen(false)
    }
  }

  const handleCancelDelete = () => {
    setRunnerToDelete(null)
    setIsOpen(false)
  }

  // Define columns for the generalized List
  const columns: ListColumn[] = [
    {
      label: 'Runner',
      flex: 2,
      align: 'left'
    },
    {
      label: 'Status',
      flex: 1,
      align: 'center',
      width: '140px'
    },
    {
      label: 'Actions',
      flex: 0,
      align: 'center',
      width: '60px'
    }
  ]

  // Map runner data for the List as arrays of ReactNode, with Typography for Runner cell
  const runnerValues = runners.map(runner => [
    // Runner cell: name and uuid stacked, with Typography
    <div key={runner.id}>
      <Typography
        fontFamily='geist'
        fontSize='labelSm'
        fontWeight='bold'
        className='text-tokens-neutral-text-primary'
      >
        {runner.name}
      </Typography>
      <Typography
        fontFamily='geistMono'
        fontSize='labelSm'
        className='text-tokens-neutral-text-tertiary'
        style={{ fontSize: 11 }}
      >
        {runner.uuid}
      </Typography>
    </div>,
    // Status cell
    <Tag key={`status-${runner.id}`} variant={runner.active ? 'success' : 'warning'}>
      {runner.active ? 'ONLINE' : 'OFFLINE'}
    </Tag>,
    // Actions cell
    <DropdownMenu key={`actions-${runner.id}`}>
      <DropdownMenuTrigger>
        <Button
          variant='borderless'
          className='flex w-[36px] h-[36px] p-0 hover:bg-transparent'
          aria-label='Runner actions menu'
        >
          <svg
            width='36'
            height='36'
            viewBox='0 0 36 36'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M11.333 18.2604C11.333 18.404 11.3613 18.5463 11.4163 18.679C11.4712 18.8117 11.5518 18.9322 11.6534 19.0338C11.7549 19.1354 11.8755 19.2159 12.0082 19.2709C12.1409 19.3259 12.2831 19.3542 12.4268 19.3542C12.5704 19.3542 12.7126 19.3259 12.8453 19.2709C12.978 19.2159 13.0986 19.1354 13.2002 19.0338C13.3017 18.9322 13.3823 18.8117 13.4373 18.679C13.4922 18.5463 13.5205 18.404 13.5205 18.2604C13.5205 18.1168 13.4922 17.9745 13.4373 17.8418C13.3823 17.7091 13.3017 17.5886 13.2002 17.487C13.0986 17.3854 12.978 17.3049 12.8453 17.2499C12.7126 17.1949 12.5704 17.1667 12.4268 17.1667C12.2831 17.1667 12.1409 17.1949 12.0082 17.2499C11.8755 17.3049 11.7549 17.3854 11.6534 17.487C11.5518 17.5886 11.4712 17.7091 11.4163 17.8418C11.3613 17.9745 11.333 18.1168 11.333 18.2604ZM16.8018 18.2604C16.8018 18.5505 16.917 18.8287 17.1221 19.0338C17.3272 19.2389 17.6054 19.3542 17.8955 19.3542C18.1856 19.3542 18.4638 19.2389 18.6689 19.0338C18.874 18.8287 18.9893 18.5505 18.9893 18.2604C18.9893 17.9703 18.874 17.6921 18.6689 17.487C18.4638 17.2819 18.1856 17.1667 17.8955 17.1667C17.6054 17.1667 17.3272 17.2819 17.1221 17.487C16.917 17.6921 16.8018 17.9703 16.8018 18.2604ZM22.2705 18.2604C22.2705 18.5505 22.3857 18.8287 22.5909 19.0338C22.796 19.2389 23.0742 19.3542 23.3643 19.3542C23.6543 19.3542 23.9325 19.2389 24.1377 19.0338C24.3428 18.8287 24.458 18.5505 24.458 18.2604C24.458 17.9703 24.3428 17.6921 24.1377 17.487C23.9325 17.2819 23.6543 17.1667 23.3643 17.1667C23.0742 17.1667 22.796 17.2819 22.5909 17.487C22.3857 17.6921 22.2705 17.9703 22.2705 18.2604Z'
              fill='black'
              fillOpacity='0.45'
            />
          </svg>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end'>
        <DropdownMenuItem onClick={() => handleEditRunner(runner)}>Edit Runner</DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleConfigure(runner)}>Configure</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => handleDeleteRunner(runner)}
          className='text-tokens-status-error-text'
          disabled={deleteRunnerMutation.isPending}
        >
          {deleteRunnerMutation.isPending ? 'Deleting...' : 'Delete Runner'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  ])

  return (
    <div className='w-full'>
      <div className='bg-tokens-neutral-fill-fill-4 rounded-[16px] h-[68px] mb-6 w-full mt-12 justify-between p-4'>
        <div className='flex flex-row items-center justify-between'>
          <div className='flex flex-row items-center flex-1 gap-3'>
            <div className='bg-tokens-status-link-base rounded-full p-1 w-6 h-6 flex items-center justify-center'>
              <svg
                width='14'
                height='14'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M20 2H4C2.89543 2 2 2.89543 2 4V8C2 9.10457 2.89543 10 4 10H20C21.1046 10 22 9.10457 22 8V4C22 2.89543 21.1046 2 20 2Z'
                  stroke='black'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                <path
                  d='M20 14H4C2.89543 14 2 14.8954 2 16V20C2 21.1046 2.89543 22 4 22H20C21.1046 22 22 21.1046 22 20V16C22 14.8954 21.1046 14 20 14Z'
                  stroke='black'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                <path
                  d='M6 6H6.01'
                  stroke='black'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                <path
                  d='M6 18H6.01'
                  stroke='black'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </div>
            <Typography
              fontFamily='geistMono'
              fontSize='labelSm'
              fontWeight='semibold'
              className='text-tokens-neutral-text-tertiary flex-1'
            >
              Register a private runner to execute tasks inside your secure environment.
            </Typography>
          </div>
          <Button
            onClick={onAddRunner}
            variant='outlined'
            leftIcon={<FiPlus className='w-4 h-4' />}
            className='ml-auto'
            aria-label='Add runner button'
          >
            Add Runner
          </Button>
        </div>
      </div>

      <List columns={columns} values={runnerValues} />

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isOpen} onClose={handleCancelDelete} hideCloseButton>
        <ModalBody className='py-8 px-6'>
          <div className='flex items-start gap-4'>
            <div className='shrink-0 mt-1'>
              <AlertIcon />
            </div>
            <div className='flex flex-col gap-12 w-full'>
              <div className='flex flex-col gap-2'>
                <Typography
                  fontFamily='geist'
                  fontSize='titleLg'
                  fontWeight='bold'
                  className='text-tokens-neutral-text-primary'
                >
                  Delete Runner
                </Typography>
                <Typography
                  fontFamily='geist'
                  fontSize='bodyLg'
                  fontWeight='medium'
                  className='text-tokens-neutral-text-primary'
                >
                  Are you sure you want to delete runner &quot;{runnerToDelete?.name}?&quot; This
                  action can not be undone.
                </Typography>
              </div>

              <div className='flex gap-2 justify-end'>
                <Button
                  variant='outlined'
                  onClick={handleCancelDelete}
                  disabled={deleteRunnerMutation.isPending}
                  aria-label='Cancel delete runner'
                >
                  Cancel
                </Button>
                <Button
                  variant='destructive'
                  onClick={handleConfirmDelete}
                  disabled={deleteRunnerMutation.isPending}
                  aria-label='Confirm delete runner'
                >
                  {deleteRunnerMutation.isPending ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </div>
  )
}

export default RunnersList
